<template>
  <div class="retention-sales-report">
    <div class="bg-white">
      <h1 class="text-2xl font-bold text-gray-900 mb-6">Retention-Satış Raporu</h1>

      <!-- Filters -->
      <div class="filters-container mb-6">
        <div class="flex gap-4 items-end">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Başlangıç Tarihi
            </label>
            <input
              v-model="startDate"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Bitiş Tarihi
            </label>
            <input
              v-model="endDate"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <button
              @click="loadReport"
              :disabled="loading"
              class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="loading">Yükleniyor...</span>
              <span v-else>Raporu Getir</span>
            </button>
          </div>
          <div>
            <button
              @click="clearCache"
              class="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cache Temizle
            </button>
          </div>
          <div v-if="isAdmin">
            <button
              @click="showBackfillPanel = !showBackfillPanel"
              class="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              Geçmişi Doldur (Backfill)
            </button>
          </div>
        </div>
      </div>

      <!-- Backfill panel (sadece admin) -->
      <div
        v-if="isAdmin && showBackfillPanel"
        class="mb-6 border border-orange-300 bg-orange-50 rounded-lg p-4"
      >
        <h3 class="text-sm font-bold text-orange-800 mb-2">
          ⚠ Backfill — Dikkatli Kullanın
        </h3>
        <ul class="text-sm text-orange-800 list-disc pl-5 space-y-1 mb-3">
          <li>
            Geçmişteki tüm onaylı yatırımları journal kayıtlarına bakarak ledger'a yazar.
            <b>Journal kaydı olmayan müşterilerde bugünkü temsilci/retention değeri kullanılır</b> —
            yani eski atamalar birebir doğru olmayabilir (best-effort).
          </li>
          <li>
            Veritabanına ciddi sorgu yükü bindirir; <b>yoğun çalışma saatlerinde çalıştırmayın</b>.
          </li>
          <li>
            Mevcut/canlı ledger kayıtlarını <b>ezmez</b> (INSERT IGNORE) — tekrar çalıştırmak güvenlidir,
            ancak daha önce backfill ile yazılmış hatalı satırları da düzeltmez.
          </li>
          <li>
            Backfill ile eklenen satırlar rapora anında dahil olur ve temsilci hanelerini
            <b>geriye dönük değiştirir</b>; raporu paylaştıysanız rakamlar oynayacaktır.
          </li>
        </ul>
        <div class="flex items-center gap-4">
          <button
            @click="runBackfill"
            :disabled="backfillRunning"
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="backfillRunning">Çalışıyor... (offset {{ backfillProgress.offset }})</span>
            <span v-else>Riskleri Anladım, Backfill'i Başlat</span>
          </button>
          <div v-if="backfillProgress.batches > 0" class="text-sm text-gray-700">
            {{ backfillProgress.processed }} müşteri işlendi,
            {{ backfillProgress.inserted }} satır eklendi
            ({{ backfillProgress.batches }} batch)
            <span v-if="backfillDone" class="text-green-700 font-semibold">— Tamamlandı ✓</span>
          </div>
        </div>
      </div>

      <!-- Summary table -->
      <div v-if="reportData && !loading" class="report-content">
        <div class="mb-4">
          <h2 class="text-lg font-semibold text-gray-800">
            Rapor Dönemi: {{ reportData.period }}
          </h2>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Temsilci
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Departman
                </th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Satış Hanesi ($)
                </th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Retention Hanesi ($)
                </th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  FTD
                </th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  İşlem Sayısı
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <template v-for="row in reportData.summary">
                <tr
                  :key="'row-' + row.user_id"
                  class="hover:bg-gray-50 cursor-pointer"
                  @click="toggleDetail(row)"
                >
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                    {{ row.name || 'Bilinmiyor (#' + row.user_id + ')' }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {{ row.department || '-' }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                    {{ formatMoney(row.sales_credit_total) }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                    {{ formatMoney(row.retention_credit_total) }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                    {{ row.ftd_count }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                    {{ row.sales_deposit_count + row.retention_deposit_count }}
                  </td>
                </tr>

                <!-- Expanded detail -->
                <tr v-if="expandedUserId === row.user_id" :key="'detail-' + row.user_id">
                  <td colspan="6" class="px-4 py-4 bg-gray-50">
                    <div v-if="detailLoading" class="flex items-center py-4">
                      <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <span class="ml-2 text-gray-600 text-sm">Detay yükleniyor...</span>
                    </div>
                    <div v-else class="space-y-6">
                      <template v-for="role in ['sales', 'retention']">
                      <div v-if="getDetailRows(row.user_id, role)" :key="role">
                        <h3 class="text-sm font-semibold text-gray-700 mb-2">
                          {{ role === 'sales' ? 'Satış Hanesi Yatırımları' : 'Retention Hanesi Yatırımları' }}
                          ({{ getDetailRows(row.user_id, role).length }})
                        </h3>
                        <div v-if="getDetailRows(row.user_id, role).length === 0" class="text-sm text-gray-500">
                          Kayıt yok.
                        </div>
                        <table v-else class="min-w-full bg-white border border-gray-200 text-sm">
                          <thead class="bg-gray-100">
                            <tr>
                              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Müşteri</th>
                              <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tutar ($)</th>
                              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">FTD Tarihi</th>
                              <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Gün</th>
                              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rozet</th>
                            </tr>
                          </thead>
                          <tbody class="divide-y divide-gray-100">
                            <tr v-for="detail in getDetailRows(row.user_id, role)" :key="detail.transaction_id">
                              <td class="px-3 py-2 whitespace-nowrap text-blue-600">
                                <router-link :to="'/leadDetail/' + detail.customer_id">
                                  {{ detail.customer_name || '#' + detail.customer_id }}
                                </router-link>
                              </td>
                              <td class="px-3 py-2 whitespace-nowrap text-right">
                                {{ formatMoney(detail.amount) }}
                              </td>
                              <td class="px-3 py-2 whitespace-nowrap">
                                {{ formatDateTime(detail.transaction_at) }}
                              </td>
                              <td class="px-3 py-2 whitespace-nowrap">
                                {{ formatDateTime(detail.ftd_at) }}
                              </td>
                              <td class="px-3 py-2 whitespace-nowrap text-right">
                                {{ detail.days_since_ftd }}
                              </td>
                              <td class="px-3 py-2 whitespace-nowrap">
                                <span
                                  v-if="detail.is_ftd == 1"
                                  class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-1"
                                >
                                  FTD
                                </span>
                                <span
                                  v-if="detail.source === 'backfill'"
                                  class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                                >
                                  Backfill
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      </template>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot class="bg-gray-50">
              <tr>
                <td class="px-4 py-3 text-sm font-bold text-gray-900" colspan="2">Toplam</td>
                <td class="px-4 py-3 text-sm text-right font-bold text-gray-900">
                  {{ formatMoney(totalSales) }}
                </td>
                <td class="px-4 py-3 text-sm text-right font-bold text-gray-900">
                  {{ formatMoney(totalRetention) }}
                </td>
                <td class="px-4 py-3 text-sm text-right font-bold text-gray-900">
                  {{ totalFtd }}
                </td>
                <td class="px-4 py-3 text-sm text-right font-bold text-gray-900">
                  {{ totalDeposits }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-2 text-gray-600">Rapor yükleniyor...</span>
      </div>

      <!-- Empty state -->
      <div
        v-if="reportData && !loading && reportData.summary.length === 0"
        class="text-center py-12"
      >
        <div class="text-gray-500 text-lg mb-2">Veri bulunamadı</div>
        <div class="text-gray-400">Seçili tarih aralığında onaylı yatırım verisi bulunmamaktadır.</div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '../services/http'

export default {
  name: 'RetentionSalesReport',
  data() {
    const today = new Date()
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

    return {
      startDate: firstDayOfMonth.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      reportData: null,
      loading: false,
      expandedUserId: null,
      detailLoading: false,
      detailCache: {},
      cache: {},
      cacheExpiry: 5 * 60 * 1000, // 5 dakika
      showBackfillPanel: false,
      backfillRunning: false,
      backfillDone: false,
      backfillProgress: { offset: 0, processed: 0, inserted: 0, batches: 0 }
    }
  },
  mounted() {
    // Yetki UX kontrolü: cache.me async yüklendiği için sadece department
    // BİLİNİYORSA ve yetkisizse redirect et. Asıl güvenlik backend guard'da.
    const me = (this.$root.cache || {}).me || {}
    if (
      me.department &&
      !['admin', 'admin1', 'admin2', 'sales_manager'].includes(me.department)
    ) {
      this.$router.push('/')
    }
  },
  computed: {
    isAdmin() {
      const me = (this.$root.cache || {}).me || {}
      return ['admin', 'admin1', 'admin2'].includes(me.department)
    },
    totalSales() {
      if (!this.reportData) return 0
      return this.reportData.summary.reduce((sum, r) => sum + r.sales_credit_total, 0)
    },
    totalRetention() {
      if (!this.reportData) return 0
      return this.reportData.summary.reduce((sum, r) => sum + r.retention_credit_total, 0)
    },
    totalFtd() {
      if (!this.reportData) return 0
      return this.reportData.summary.reduce((sum, r) => sum + r.ftd_count, 0)
    },
    totalDeposits() {
      if (!this.reportData) return 0
      return this.reportData.summary.reduce(
        (sum, r) => sum + r.sales_deposit_count + r.retention_deposit_count,
        0
      )
    }
  },
  methods: {
    getCacheKey() {
      return `retention_sales_${this.startDate}_${this.endDate}`
    },

    getFromCache(key) {
      const cached = this.cache[key]
      if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
        return cached.data
      }
      return null
    },

    saveToCache(key, data) {
      this.cache[key] = {
        data: data,
        timestamp: Date.now()
      }
    },

    clearCache() {
      this.cache = {}
      this.detailCache = {}
      alert('Cache temizlendi')
    },

    async runBackfill() {
      const onay = confirm(
        'Backfill geçmişteki TÜM onaylı yatırımları ledger tablosuna yazacak.\n\n' +
          '- Journal kaydı olmayan müşterilerde bugünkü temsilci/retention değeri kullanılır (geçmiş birebir doğru olmayabilir).\n' +
          '- Veritabanına yük bindirir; yoğun saatte çalıştırmayın.\n' +
          '- Rapor rakamları geriye dönük değişecektir.\n\n' +
          'Devam etmek istiyor musunuz?'
      )
      if (!onay) return

      this.backfillRunning = true
      this.backfillDone = false
      this.backfillProgress = { offset: 0, processed: 0, inserted: 0, batches: 0 }

      try {
        let offset = 0
        // next_offset null dönene kadar batch'ler halinde ilerle
        while (offset !== null && offset !== undefined) {
          this.backfillProgress.offset = offset
          const response = await http.runRetentionSalesBackfill({
            offset: offset,
            limit: 500
          })
          if (!response.data.success) {
            alert(response.data.message || 'Backfill sırasında hata oluştu')
            return
          }
          this.backfillProgress.processed += response.data.processed_customers
          this.backfillProgress.inserted += response.data.inserted_rows
          this.backfillProgress.batches += 1
          offset = response.data.next_offset
        }
        this.backfillDone = true
        // Rapor cache'lerini boşalt ki yeni rakamlar görünsün
        this.cache = {}
        this.detailCache = {}
        if (this.reportData) {
          this.loadReport()
        }
      } catch (error) {
        console.error('Backfill error:', error)
        alert('Backfill sırasında bir hata oluştu (console kontrol edin). Kaldığı yerden tekrar başlatmak güvenlidir.')
      } finally {
        this.backfillRunning = false
      }
    },

    async loadReport() {
      if (!this.startDate || !this.endDate) {
        alert('Lütfen başlangıç ve bitiş tarihlerini seçin')
        return
      }

      this.loading = true
      this.expandedUserId = null
      this.detailCache = {}

      const cacheKey = this.getCacheKey()
      const cachedData = this.getFromCache(cacheKey)

      if (cachedData) {
        this.reportData = cachedData
        this.loading = false
        return
      }

      try {
        const response = await http.getRetentionSalesReport({
          start_date: this.startDate,
          end_date: this.endDate
        })

        if (response.data.success) {
          this.reportData = response.data
          this.saveToCache(cacheKey, response.data)
        } else {
          alert(response.data.message || 'Bir hata oluştu')
        }
      } catch (error) {
        console.error('Report loading error:', error)
        alert('Rapor yüklenirken bir hata oluştu')
      } finally {
        this.loading = false
      }
    },

    detailKey(userId, role) {
      return `${userId}_${role}_${this.startDate}_${this.endDate}`
    },

    getDetailRows(userId, role) {
      const cached = this.detailCache[this.detailKey(userId, role)]
      return cached ? cached : null
    },

    async toggleDetail(row) {
      if (this.expandedUserId === row.user_id) {
        this.expandedUserId = null
        return
      }
      this.expandedUserId = row.user_id

      const roles = []
      if (row.sales_deposit_count > 0 && !this.getDetailRows(row.user_id, 'sales')) {
        roles.push('sales')
      }
      if (row.retention_deposit_count > 0 && !this.getDetailRows(row.user_id, 'retention')) {
        roles.push('retention')
      }
      if (roles.length === 0) return

      this.detailLoading = true
      try {
        for (const role of roles) {
          const response = await http.getRetentionSalesReportDetail({
            user_id: row.user_id,
            role: role,
            start_date: this.startDate,
            end_date: this.endDate
          })
          if (response.data.success) {
            this.$set(this.detailCache, this.detailKey(row.user_id, role), response.data.rows)
          } else {
            alert(response.data.message || 'Detay yüklenemedi')
          }
        }
      } catch (error) {
        console.error('Detail loading error:', error)
        alert('Detay yüklenirken bir hata oluştu')
      } finally {
        this.detailLoading = false
      }
    },

    formatMoney(value) {
      const num = Number(value) || 0
      return num.toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString.replace(' ', 'T'))
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.retention-sales-report {
  @apply max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8;
}

.filters-container {
  @apply bg-gray-50 rounded-lg p-4;
}

.report-content {
  @apply mt-6;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
